import * as React from 'react'
import { Box, Stack, Avatar, IconButton, Tooltip, Button } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useNavigate } from 'react-router-dom'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import DataGridTable from './DataGridTable'
import ListToolbar from './ListToolBar'
import FilterPopover from './FilterPopover'
import KebabMenu from './KebabMenu'
import StatusBtn from '../atoms/status/StatusBtn'
import styles from '../../../src/pages/inviteStaff/InviteStaffPage.module.scss'
import { toastHelper } from '../../util/toastHelper'
import SelectCustom from '../atoms/select/SelectCustom'

export default function StaffDataGrid({ staff = [], handleStatusChange, onRemove }) {
	const navigate = useNavigate()

	const rows = React.useMemo(() => {
		return (staff || []).map((m) => {
			const name = m.name || m.email || 'Unknown'
			return {
				id: m._id ?? `inv-${m.email}`,
				name,
				avatar: m.profilePic || '',
				role: 'Team Member',
				status: m.status,
				_raw: m,
			}
		})
	}, [staff])

	// search and filters
	const [search, setSearch] = React.useState('')
	const [statusFilter, setStatusFilter] = React.useState([])
	const [roleFilter, setRoleFilter] = React.useState([])

	const availableRoles = React.useMemo(() => {
		const set = new Set(rows.map((r) => r.role))
		return Array.from(set).sort()
	}, [rows])

	const filteredRows = React.useMemo(() => {
		const q = search.trim().toLowerCase()
		return rows.filter((r) => {
			if (q && !r.name.toLowerCase().includes(q)) return false
			if (statusFilter.length && !statusFilter.includes(r.status))
				return false
			if (roleFilter.length && !roleFilter.includes(r.role)) return false
			return true
		})
	}, [rows, search, statusFilter, roleFilter])

	const chips = [
		...statusFilter.map((s) => {
			const status = s === 'active' ? 'Active' : 'Inactive'
			return {
				key: `status-${s}`,
				label: `Status ${status}`,
				onDelete: () =>
					setStatusFilter((prev) => prev.filter((x) => x !== s)),
			}
		}),
		...roleFilter.map((r) => ({
			key: `role-${r}`,
			label: `Roles ${r}`,
			onDelete: () =>
				setRoleFilter((prev) => prev.filter((x) => x !== r)),
		})),
	]
	const clearFilters = () => {
		setStatusFilter([])
		setRoleFilter([])
	}

	// filter popover
	const [filterAnchor, setFilterAnchor] = React.useState(null)
	const openFilter = (e) => setFilterAnchor(e.currentTarget)
	const closeFilter = () => setFilterAnchor(null)

	// export current view
	const exportCSV = () => {
		const header = ['Name', 'Role', 'Status']
		const rowsCSV = filteredRows.map((r) => [r.name, r.role, r.status])
		const csv = [header, ...rowsCSV]
			.map((r) => r.map(esc).join(','))
			.join('\n')
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'members.csv'
		a.click()
		URL.revokeObjectURL(url)
	}

	// kebab menu
	const [menuAnchor, setMenuAnchor] = React.useState(null)
	const [menuRow, setMenuRow] = React.useState(null)
	const openMenu = (e, row) => {
		e.stopPropagation()
		setMenuAnchor(e.currentTarget)
		setMenuRow(row)
	}
	const closeMenu = () => {
		setMenuAnchor(null)
		setMenuRow(null)
	}

	const status_options = [
		{
			value: 'active',
			label: (
				<StatusBtn
					label="Active"
					variant="active"
					className={styles.statusOption}
				/>
			),
		},
		{
			value: 'inactive',
			label: (
				<StatusBtn
					label="Inactive"
					variant="closed"
					className={styles.statusOption}
				/>
			),
		},
	]

	const columns = React.useMemo(
		() => [
			{
				field: 'name',
				headerName: 'Name',
				flex: 1.6,
				minWidth: 160,
				sortable: true,
				renderCell: (params) => {
					const { avatar, name } = params.row
					return (
						<Stack
							direction="row"
							spacing={1.5}
							alignItems="center"
							sx={{ height: '100%' }}
							className="img-with-text-wrp"
						>
							<Avatar
								src={
									avatar ||
									'https://static.vecteezy.com/system/resources/previews/020/911/740/non_2x/user-profile-icon-profile-avatar-user-icon-male-icon-face-icon-profile-icon-free-png.png'
								}
								sx={{
									width: 40,
									height: 40,
									borderRadius: '50%',
									'@media (max-width: 767px)': {
										width: 30,
										height: 30,
									},
								}}
							/>
							<Box
								component="span"
								sx={{ fontWeight: 600 }}
								className="img-with-text"
							>
								{name}
							</Box>
						</Stack>
					)
				},
			},
			{
				field: 'role',
				headerName: 'Roles',
				flex: 1,
				minWidth: 200,
				sortable: true,
				renderCell: (params) => (
					<span className="role-text">{params.value || 'User'}</span>
				),
			},
			// {
			// 	field: 'status',
			// 	headerName: 'Status',
			// 	flex: 0.8,
			// 	minWidth: 130,
			// 	sortable: true,
			// 	renderCell: (params) => {
			// 		return (
			// 			<StatusBtn
			// 				label={params.value}
			// 				variant={params.value === 'active' ? 'active' : 'closed'}
			// 				size="small"
			// 			/>
			// 		)
			// 	},
			// },
			{
				field: 'status',
				headerName: 'Status',
				flex: .4,
				minWidth: 140,
				renderCell: (params) => {
					const status = params.value
					const row = params.row._raw
					return (
						<SelectCustom
							options={status_options}
							defaultValue={status}
							setDataFunction={(newStatus) =>
								handleStatusChange(newStatus?.value, row)
							}
							renderValueAsLabel={true}
						/>
					)
				},
			},
			// {
			// 	field: 'accept',
			// 	headerName: '',
			// 	sortable: false,
			// 	filterable: false,
			// 	width: 120,
			// 	renderCell: (params) => {
			// 		const r = params.row
			// 		if (r.statusRaw !== 'recieved') return null
			// 		return (
			// 			<Tooltip title="Accept request">
			// 				<Button
			// 					data-cy="accept"
			// 					onClick={(e) => {
			// 						e.stopPropagation()
			// 						onAccept?.(r.id)
			// 					}}
			// 					size="small"
			// 					startIcon={<CheckCircleOutlineIcon />}
			// 					variant="text"
			// 					sx={{ minWidth: 0, p: 0.5, lineHeight: 1 }}
			// 				>
			// 					Accept
			// 				</Button>
			// 			</Tooltip>
			// 		)
			// 	},
			// },
			{
				field: 'menu',
				headerName: '',
				sortable: false,
				filterable: false,
				flex: .2,
				width: 64,
				align: 'right',
				renderCell: (params) => (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							width: '100%',
							height: '100%',
						}}
					>
						<IconButton
							data-cy="kebab"
							size="small"
							onClick={(e) => openMenu(e, params.row)}
							sx={{ p: 0.5 }}
						>
							<img src="/images/more.svg" alt="" />
						</IconButton>
					</Box>
				),
			},
		],
		[status_options, handleStatusChange]
	)

	return (
		<Box className={styles.dataGridWrp}>
			<ListToolbar
				title="Staff"
				searchPlaceholder="Search staff"
				searchValue={search}
				onSearchChange={setSearch}
				chips={chips}
				onClearFilters={clearFilters}
				onOpenFilter={openFilter}
				onExport={exportCSV}
			/>

			<FilterPopover
				anchorEl={filterAnchor}
				open={Boolean(filterAnchor)}
				onClose={closeFilter}
				onClear={clearFilters}
				onApply={closeFilter}
				sections={[
					{
						id: 'status',
						title: 'Status',
						options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }],
						selected: statusFilter,
						onToggle: (val, checked) =>
							setStatusFilter((prev) =>
								checked
									? [...prev, val]
									: prev.filter((x) => x !== val)
							),
					},
					{
						id: 'roles',
						title: 'Roles',
						options: availableRoles,
						selected: roleFilter,
						onToggle: (val, checked) =>
							setRoleFilter((prev) =>
								checked
									? [...prev, val]
									: prev.filter((x) => x !== val)
							),
					},
				]}
			/>

			<DataGridTable rows={filteredRows} columns={columns} />

			<KebabMenu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={closeMenu}
				items={[
					{
						key: 'chatNow',
						label: 'Chat Now',
						icon: (
							<>
								<img src="/images/chat-icon.svg" alt="chat" />
							</>
						),
						onClick: () => {
							if (!menuRow?._raw?.staffId) {
								toastHelper('User has not registered yet', 'error')
								return
							}
							navigate(`/chat`, {
								state: {
									selectedUser: {
										...menuRow._raw,
										_id: menuRow._raw.staffId,
										preferredName: menuRow._raw.name,
										avatar: menuRow._raw.profilePic || ''
									}
								}
							})
						},
					},
					{
						key: 'profile',
						label: 'Profile',
						icon: (
							<>
								<img src="/images/assign-task.svg" alt="" />
							</>
						),
						onClick: () =>
							menuRow?._raw?.staffId
								? navigate(`/profile/${menuRow._raw.staffId}`)
								: toastHelper('No profile found', 'error'),
					},
					{
						key: 'delete',
						label: 'Delete',
						icon: <DeleteOutlineIcon fontSize="small" />,
						onClick: () => onRemove?.(menuRow?._raw),
						color: 'error.main',
					},
				]}
			/>
		</Box>
	)
}

function esc(s) {
	const str = String(s ?? '')
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}
